/**
 * canvas-engine/migration/sectionMigration.js
 *
 * OWN load-time Section_Migration for the Enterprise Canvas Builder (Task 4.1).
 *
 * Converts a Template's Sections into Canvas_Sections at load time:
 *   - An already-canvas Section passes through UNCHANGED (idempotent — Req 6.2,
 *     6.4). It is never re-decomposed.
 *   - A non-canvas Section is DECOMPOSED into individually editable
 *     Canvas_Elements via the own `decompositionAdapters` (Req 6.1, 6.3),
 *     reproducing the section's own stored content/style/layout look. The
 *     decomposition is wrapped in try/catch and falls back to the own generic
 *     decomposition so migration NEVER throws (Req 6.5). A `native` element is
 *     never produced (the decomposition engine never emits one).
 *   - The original Section fields (sectionId, type, visible, layout, …) are
 *     preserved on a NEW section object; the decomposed model is written to
 *     `content.canvas` and the original type is recorded at
 *     `content.sourceType` for provenance. The input is never mutated.
 *
 * ZERO-REUSE CONSTRAINT: this module imports ONLY its own engine siblings
 * (`../model/canvasModel`, `../presets/decompositionAdapters`). It imports
 * NOTHING from `admin-console-ui/src/modules/template-builder/` (e.g. the
 * existing `migrateToCanvas.js` / `migration/families/*`) or from
 * `academy-ui/src/template/`.
 *
 * Pure aside from the id generation performed deep inside `normalizeCanvas`
 * (which only fills missing element ids). Every exported function returns NEW
 * values and never mutates its inputs.
 *
 * Public surface (design: Components and Interfaces — migration/sectionMigration):
 *   - isCanvasSection(section)
 *   - migrateSectionToCanvas(section, ctx)
 *   - migrateTemplateToCanvas(template, domainType)
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5
 */

import { isCanvasModel, normalizeCanvas } from '../model/canvasModel';
import { decomposeSection, genericToCanvas } from '../presets/decompositionAdapters';

// ── Small pure helpers ──────────────────────────────────────────────────────

/** True for a non-null, non-array plain object. */
function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Trimmed string, or '' when not a usable string. */
function pickString(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Resolve the section type used to route decomposition + record provenance.
 * Prefers the section's own `type` (then `sectionType`); otherwise falls back
 * to a caller-supplied ctx/domain hint, then to `'generic'`. (Req 6.1, 6.5)
 *
 * @param {object} section
 * @param {object} ctx
 * @returns {string}
 */
function resolveSectionType(section, ctx) {
  const fromSection = pickString(section.type) || pickString(section.sectionType);
  if (fromSection) return fromSection;
  const fromCtx = pickString(ctx.sectionType) || pickString(ctx.domainType) || pickString(ctx.domain);
  if (fromCtx) return fromCtx;
  return 'generic';
}

/**
 * Build the DATA descriptor handed to the decomposition adapter. Passing a
 * descriptor (rather than a bare type string) makes the adapter reproduce THIS
 * section's stored look — preserving its content, style, and layout data
 * (Req 6.3) — instead of the family's default preset values.
 *
 * @param {object} section
 * @param {string} sectionType
 * @returns {object} a `buildSectionCanvas`-shaped descriptor
 */
function buildDescriptor(section, sectionType) {
  const descriptor = {
    sectionType,
    content: isPlainObject(section.content) ? section.content : {},
    style: isPlainObject(section.style) ? section.style : {},
    layout: isPlainObject(section.layout) ? section.layout : {},
  };
  if (isPlainObject(section.binding)) {
    descriptor.binding = section.binding;
  }
  return descriptor;
}

/**
 * Decompose a non-canvas section into a Canvas_Model, never throwing. On any
 * decomposition error, falls back to the own generic decomposition; as a final
 * guard it calls `genericToCanvas` with an empty descriptor (always a valid,
 * minimal Canvas_Model). (Req 6.5)
 *
 * @param {object} descriptor
 * @param {string} sectionType
 * @param {object} ctx
 * @returns {object} a normalized Canvas_Model
 */
function decomposeSafely(descriptor, sectionType, ctx) {
  const options = { ...ctx, sectionType };
  try {
    return decomposeSection(descriptor, options);
  } catch (primaryError) {
    // Fall back to the own generic decomposition, preserving the section's
    // stored content/style/layout where the generic stack can use it.
    try {
      return decomposeSection({ ...descriptor, sectionType: 'generic' }, { ...ctx, sectionType: 'generic' });
    } catch (fallbackError) {
      // Last-resort guard: an empty generic descriptor always yields a valid,
      // minimal Canvas_Model so migration can never break. (Req 6.5)
      return genericToCanvas({}, { sectionType: 'generic' });
    }
  }
}

// ── Public surface ───────────────────────────────────────────────────────────

/**
 * True when a Section's stored data is already a Canvas_Model (the shape stored
 * at `section.content.canvas`). Such sections must NOT be re-decomposed.
 * (Req 6.2, 6.4)
 *
 * @param {*} section
 * @returns {boolean}
 */
export function isCanvasSection(section) {
  return isPlainObject(section) && isPlainObject(section.content) && isCanvasModel(section.content.canvas);
}

/**
 * Migrate a single Section into a Canvas_Section.
 *
 *   - Non-object input is returned unchanged.
 *   - An already-canvas Section is passed through UNCHANGED (idempotent — the
 *     same reference is returned and `content.canvas` is preserved as-is, so
 *     re-migrating yields an equivalent model). (Req 6.2, 6.4)
 *   - A non-canvas Section is decomposed into editable Canvas_Elements and
 *     returned as a NEW section preserving the original fields, with
 *     `content.canvas` set to the decomposed model and `content.sourceType`
 *     recording the original type for provenance. The input is never mutated;
 *     decomposition never throws and never emits a `native` element. (Req 6.1,
 *     6.3, 6.5)
 *
 * @param {*} section a Template Section
 * @param {object} [ctx] optional hints (`sectionType`, `domainType`, `domain`)
 * @returns {*} the migrated section (or the input unchanged when not an object)
 */
export function migrateSectionToCanvas(section, ctx = {}) {
  if (!isPlainObject(section)) {
    return section;
  }

  // Idempotent pass-through for already-canvas sections (Req 6.2, 6.4).
  if (isCanvasSection(section)) {
    return section;
  }

  // NO DATA LOSS: if the section already carries a canvas-LIKE model (a `content.canvas`
  // with an `elements` array) that is not strictly valid — e.g. an older payload missing
  // `schemaVersion`, or with a few un-normalized fields — REPAIR it in place via
  // `normalizeCanvas` (which preserves every element id, position, style, and the frame)
  // instead of re-decomposing from scratch. Re-decomposition would regenerate ids and
  // reset positions, discarding the author's work. We only build a brand-new model when
  // there is no usable canvas to preserve.
  const existingCanvas = isPlainObject(section.content) ? section.content.canvas : undefined;
  if (isPlainObject(existingCanvas) && Array.isArray(existingCanvas.elements)) {
    const repaired = normalizeCanvas(existingCanvas);
    const originalContent = section.content;
    return {
      ...section,
      content: {
        ...originalContent,
        canvas: repaired,
        sourceType: originalContent.sourceType !== undefined ? originalContent.sourceType : section.type,
      },
    };
  }

  const safeCtx = isPlainObject(ctx) ? ctx : {};
  const sectionType = resolveSectionType(section, safeCtx);
  const descriptor = buildDescriptor(section, sectionType);
  const canvas = decomposeSafely(descriptor, sectionType, safeCtx);

  // New section: preserve every original field, set content.canvas + provenance.
  const originalContent = isPlainObject(section.content) ? section.content : {};
  return {
    ...section,
    content: {
      ...originalContent,
      canvas,
      sourceType: section.type,
    },
  };
}

/**
 * Migrate an entire Template by mapping every page's sections through
 * `migrateSectionToCanvas`. Returns a NEW template (pages and sections copied,
 * never mutated). Missing / non-array `pages` and `sections` are tolerated:
 * absent arrays are left as-is, and non-object pages/sections pass through
 * unchanged.
 *
 * @param {*} template a Template
 * @param {string} [domainType] optional domain hint forwarded as migration ctx
 * @returns {*} a new migrated template (or the input unchanged when not an object)
 */
export function migrateTemplateToCanvas(template, domainType) {
  if (!isPlainObject(template)) {
    return template;
  }

  const ctx = pickString(domainType) ? { domainType: pickString(domainType) } : {};
  const next = { ...template };

  if (Array.isArray(template.pages)) {
    next.pages = template.pages.map((page) => {
      if (!isPlainObject(page)) return page;
      const newPage = { ...page };
      if (Array.isArray(page.sections)) {
        newPage.sections = page.sections.map((s) => migrateSectionToCanvas(s, ctx));
      }
      return newPage;
    });
  }

  return next;
}

export default migrateSectionToCanvas;
